/**
 * The operator console's own fetch layer.
 *
 * ── Why not `api/client.ts` ─────────────────────────────────────────────────
 *
 * That client is built for a clinic user: it holds a clinic access token,
 * refreshes it against `/auth/refresh`, and attaches `x-branch-id` to
 * branch-scoped calls. An operator has no clinic, no branch and no refresh
 * token — their session is a short, revocable one (QA20-3) — so every one of those
 * behaviours is either meaningless or actively wrong here.
 *
 * **The two sessions are kept apart deliberately.** Separate storage keys,
 * separate token variables, and no code path that could attach one to the
 * other's requests. Mixing them is the bug this file exists to make
 * impossible — and the API would refuse anyway, since the two token types are
 * signed with different secrets.
 */

import type {
  ClinicModuleDto,
  ClinicPreviewDto,
  GatedModule,
  HelpQuestionsReport,
  MailUsage,
  ModuleName,
  MonthlyBill,
  OverrideRow,
  PriceKind,
  SubscriptionState,
} from "@dentacore/shared-types";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3000";

/**
 * Where the operator session lives.
 *
 * `sessionStorage`, not `localStorage`, and not the key the clinic app uses.
 * An operator session reaches every tenant on the platform, so it ends when the
 * tab does rather than persisting on a shared machine.
 */
const TOKEN_KEY = "dentacore.operator.token";

export class OperatorApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "OperatorApiError";
  }
}

export interface OperatorSession {
  token: string;
  name: string;
  email: string;
}

export interface ClinicSummary {
  isSuspended: boolean;
  id: string;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  isDemo: boolean;
  createdAt: string;
  createdBy: string | null;
  branchCount: number;
  userCount: number;
  patientCount: number;
  canTakeBookings: boolean;
}

export interface NewClinic {
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  defaultCountry?: string;
  locale?: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  /**
   * A password the operator chooses and hands over (decision SA1). Blank means
   * the older flow: an inactive account and a single-use link in the post.
   */
  temporaryPassword?: string;
}

export interface CreatedClinic {
  emailed: boolean;
  id: string;
  name: string;
  slug: string;
  adminEmail: string;
  /** All three empty when a password was issued — there is no link to hand over. */
  inviteToken: string;
  inviteExpiresAt: string;
  /**
   * The whole link, from the server, at the clinic's own subdomain.
   *
   * **Displayed verbatim.** This used to be built here as
   * `${window.location.origin}/accept/${token}` — and the operator console is
   * always at the apex, so every link it showed pointed somewhere the token
   * cannot resolve (QA20-5).
   */
  inviteUrl: string;
  issuedPassword: boolean;
  /**
   * True when the address already had a DentaCore login, so this practice was
   * added to it rather than a new one being created (I2).
   *
   * Changes what the operator says when handing the invite over: a brand-new
   * admin is told they will choose a password, and somebody who already works
   * elsewhere must be told the opposite, or they will hunt for one nobody set.
   */
  joinedExistingAccount: boolean;
  /** Where this clinic signs in, which is what the operator reads out. */
  signInUrl: string;
}

export const storedToken = (): string | null => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // A browser with site data blocked. The console still works for the length
    // of one page; it simply will not survive a reload.
    return null;
  }
};

const remember = (token: string | null): void => {
  try {
    if (token === null) sessionStorage.removeItem(TOKEN_KEY);
    else sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* see `storedToken` */
  }
};

async function call<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const token = storedToken();

  const response = await fetch(`${API_URL}${path}`, {
    method: options.method ?? "GET",
    headers: {
      "content-type": "application/json",
      ...(token === null ? {} : { authorization: `Bearer ${token}` }),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text === "" ? null : JSON.parse(text);
  } catch {
    payload = text;
  }

  if (!response.ok) {
    // A 401 on anything other than sign-in means the session is over. Clearing
    // it here rather than at the call site means every screen recovers the same
    // way — by finding no token and showing the sign-in form.
    if (response.status === 401 && !path.endsWith("/sign-in")) remember(null);

    const message = messageOf(payload) ?? `Request failed (${String(response.status)})`;
    throw new OperatorApiError(response.status, message);
  }

  return payload as T;
}

/** The API returns a string or an array of them, depending on the validator. */
function messageOf(payload: unknown): string | null {
  if (payload === null || typeof payload !== "object") return null;
  const message = (payload as { message?: unknown }).message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.map(String).join(". ");
  return null;
}

export async function signIn(email: string, password: string): Promise<OperatorSession> {
  const session = await call<{ token: string; name: string; email: string }>("/operator/sign-in", {
    method: "POST",
    body: { email, password },
  });
  remember(session.token);
  return session;
}

/**
 * End the session — **on the server as well as here** (QA20-3).
 *
 * Forgetting the token used to be the whole of signing out, so it stayed valid
 * for the rest of its life and a copied one reached every tenant on the
 * platform. The local clear happens first and unconditionally: whatever the
 * network does, this browser is signed out.
 */
export function signOut(): void {
  const token = storedToken();
  remember(null);
  if (token === null) return;

  // Fire and forget, and deliberately not awaited. The session is already gone
  // from this browser; a slow or failed request must not leave somebody
  // staring at a console they think they have left.
  void fetch(`${API_URL}/operator/sign-out`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
    keepalive: true,
  }).catch(() => {
    /* The row expires on its own within the idle window. */
  });
}

/** Resume a session a reload interrupted, or report that there is none. */
export async function restore(): Promise<OperatorSession | null> {
  const token = storedToken();
  if (token === null) return null;

  try {
    const who = await call<{ name: string; email: string }>("/operator/me");
    return { token, name: who.name, email: who.email };
  } catch {
    return null;
  }
}

export const listClinics = (): Promise<ClinicSummary[]> =>
  call<ClinicSummary[]>("/operator/clinics");

export const createClinic = (clinic: NewClinic): Promise<CreatedClinic> =>
  call<CreatedClinic>("/operator/clinics", { method: "POST", body: clinic });

/**
 * Does this address already have a DentaCore login? (I2, 2026-09-20.)
 *
 * Asked while the operator is still typing, so the provisioning form can drop
 * the one-time password field for somebody who already has a password — see
 * `createClinic` on the API side for what happens if one is set anyway.
 *
 * **Operator-only, and that is what makes it safe to exist.** The same question
 * asked from a clinic's People screen would be an enumeration oracle; a
 * platform admin can already list every person at every clinic from this
 * console, so it discloses nothing new to them.
 */
export const addressInUse = async (email: string, signal?: AbortSignal): Promise<boolean> => {
  const { exists } = await call<{ exists: boolean }>(
    `/operator/identity?email=${encodeURIComponent(email)}`,
    { signal },
  );
  return exists;
};

/** Where a clinic's admin has got to — the main support question. */
export type AdminState = "ACTIVE" | "INVITED" | "EXPIRED";

export interface ClinicAdmin {
  id: string;
  name: string;
  email: string;
  state: AdminState;
  inviteExpiresAt: string | null;
}

/**
 * One unresolved notice a clinic is carrying (G17).
 *
 * **The same rows the practice sees on Home**, read across every clinic — so a
 * practice whose Google calendar keeps disagreeing with DentaCore is visible
 * here before anybody rings up about it. Nothing is written for the console's
 * benefit, so it cannot show a problem the practice has not been told about.
 */
export interface ClinicNotice {
  id: string;
  kind: string;
  severity: "INFO" | "WARNING" | "CRITICAL";
  title: string;
  detail: string | null;
  branchName: string | null;
  raisedAt: string;
}

export interface ClinicDetail extends ClinicSummary {
  defaultCountry: string | null;
  suspendedReason: string | null;
  suspendedAt: string | null;
  suspendedBy: string | null;
  admins: ClinicAdmin[];
  /** Unresolved, most severe first. Empty is the ordinary, healthy case. */
  notices: ClinicNotice[];
  /** All three add-ons, on or off, with who last switched each (STORY-188a). */
  modules: ClinicModuleDto[];
  /** Preview modules — Tasks — switched like an add-on, never priced (TK-D28). */
  previews: ClinicPreviewDto[];
}

export interface ReissuedInvite {
  email: string;
  inviteToken: string;
  inviteExpiresAt: string;
  /** At the clinic's own subdomain, from the server. Displayed verbatim (QA20-5). */
  inviteUrl: string;
  emailed: boolean;
}

export interface OperatorSummary {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  lastLoginAt: string | null;
  clinicsProvisioned: number;
}

/** One person at a clinic, as the console sees them (QA20-11). */
export interface ClinicPerson {
  id: string;
  name: string;
  email: string;
  /** Clinical role. Since 2026-09-07 it says nothing about what they may reach. */
  role: string;
  canAdminister: boolean;
  isActive: boolean;
}

export interface PlatformHealth {
  outboxPending: number;
  outboxDeadLettered: number;
  oldestPendingAgeSeconds: number | null;
  clinics: number;
  suspendedClinics: number;
}

export const clinicDetail = (id: string): Promise<ClinicDetail> =>
  call<ClinicDetail>(`/operator/clinics/${id}`);

export const updateClinic = (
  id: string,
  patch: { name?: string; timezone?: string; currency?: string; defaultCountry?: string },
): Promise<ClinicDetail> =>
  call<ClinicDetail>(`/operator/clinics/${id}`, { method: "PATCH", body: patch });

/** `email` omitted means "send to whatever is on file" — a link that lapsed. */
export const reissueInvite = (
  clinicId: string,
  userId: string,
  email?: string,
): Promise<ReissuedInvite> =>
  call<ReissuedInvite>(`/operator/clinics/${clinicId}/admins/${userId}/reissue`, {
    method: "POST",
    body: email === undefined ? {} : { email },
  });

/**
 * Everybody at one clinic, and the one flag an operator may change (QA20-11).
 *
 * Deliberately not the clinic's own People endpoint — that carries phone
 * numbers, branch memberships and assistant links, none of which an operator
 * has any business reading.
 */
export const clinicPeople = (clinicId: string): Promise<ClinicPerson[]> =>
  call<ClinicPerson[]>(`/operator/clinics/${clinicId}/people`);

/** Promote only. There is no route that takes adminship away — see the API. */
export const grantAdmin = (clinicId: string, userId: string): Promise<ClinicPerson[]> =>
  call<ClinicPerson[]>(`/operator/clinics/${clinicId}/people/${userId}/grant-admin`, {
    method: "POST",
    body: {},
  });

export const suspendClinic = (id: string, reason: string): Promise<ClinicDetail> =>
  call<ClinicDetail>(`/operator/clinics/${id}/suspend`, { method: "POST", body: { reason } });

/** Switch one add-on on or off. Nothing is deleted either way (MOD-D12). */
export const setClinicModule = (
  id: string,
  module: GatedModule,
  enabled: boolean,
): Promise<ClinicDetail> =>
  call<ClinicDetail>(`/operator/clinics/${id}/modules/${module}`, {
    method: "PUT",
    body: { enabled },
  });

export const reactivateClinic = (id: string): Promise<ClinicDetail> =>
  call<ClinicDetail>(`/operator/clinics/${id}/reactivate`, { method: "POST", body: {} });

export const listOperators = (): Promise<OperatorSummary[]> =>
  call<OperatorSummary[]>("/operator/operators");

export const deactivateOperator = (id: string): Promise<void> =>
  call<void>(`/operator/operators/${id}/deactivate`, { method: "POST", body: {} });

export const platformHealth = (): Promise<PlatformHealth> =>
  call<PlatformHealth>("/operator/health");

/**
 * How much mail has gone out, and from which sender (2026-09-20).
 *
 * Counted from rows the API writes as each message leaves, not read from the
 * relay — so it survives a provider swap and can be grouped by clinic, which
 * no provider dashboard can do. Advisory: nothing refuses a send.
 */
export const mailUsage = (): Promise<MailUsage> => call<MailUsage>("/operator/mail-usage");

/* ── Pricing (STORY-190a) ───────────────────────────────────────────────── */

export interface PriceListRow {
  id: string;
  kind: PriceKind;
  module: ModuleName | null;
  branchesFrom: number | null;
  branchesTo: number | null;
  priceCents: number;
  effectiveFrom: string;
  createdByName: string | null;
  createdAt: string;
}

export interface ClinicBilling {
  branchCount: number;
  billed: ModuleName[];
  bill: MonthlyBill;
  overrides: (OverrideRow & { id: string; createdByName: string | null })[];
}

export interface BandInput {
  branchesFrom: number;
  branchesTo: number | null;
  priceCents: number;
}

export const listPrices = (): Promise<PriceListRow[]> => call<PriceListRow[]>("/operator/prices");

export const saveBands = (bands: BandInput[]): Promise<PriceListRow[]> =>
  call<PriceListRow[]>("/operator/prices/base", { method: "PUT", body: { bands } });

export const saveBundlePrice = (priceCents: number): Promise<PriceListRow[]> =>
  call<PriceListRow[]>("/operator/prices/bundle", { method: "PUT", body: { priceCents } });

export const saveModulePrice = (module: ModuleName, priceCents: number): Promise<PriceListRow[]> =>
  call<PriceListRow[]>(`/operator/prices/modules/${module}`, {
    method: "PUT",
    body: { priceCents },
  });

export const clinicBilling = (clinicId: string): Promise<ClinicBilling> =>
  call<ClinicBilling>(`/operator/clinics/${clinicId}/billing`);

/** `item` is `base`, `bundle` or a module name. A null price ends the override. */
export const saveOverride = (
  clinicId: string,
  item: "base" | "bundle" | ModuleName,
  priceCents: number | null,
  note?: string,
): Promise<ClinicBilling> => {
  const path = item === "base" || item === "bundle" ? item : `modules/${item}`;
  return call<ClinicBilling>(`/operator/clinics/${clinicId}/prices/${path}`, {
    method: "PUT",
    body: { priceCents, note },
  });
};

/* ── Subscription state (STORY-190b) ────────────────────────────────────── */

export interface SubscriptionView {
  state: SubscriptionState;
  dueOn: string | null;
  graceDays: number;
  graceEndsOn: string | null;
  reason: string | null;
  changedByName: string | null;
  changedAt: string | null;
  daysOverdue: number;
  history: {
    fromState: SubscriptionState;
    toState: SubscriptionState;
    dueOn: string | null;
    reason: string | null;
    byName: string | null;
    at: string;
  }[];
}

export interface OverdueClinic {
  clinicId: string;
  name: string;
  slug: string;
  state: SubscriptionState;
  dueOn: string;
  daysOverdue: number;
}

export interface BillingSummary {
  expectedMonthlyCents: number;
  clinicsWithTotal: number;
  clinicsWithoutPrice: number;
  byState: Record<SubscriptionState, number>;
}

export const clinicSubscription = (clinicId: string): Promise<SubscriptionView> =>
  call<SubscriptionView>(`/operator/clinics/${clinicId}/subscription`);

export const saveSubscription = (
  clinicId: string,
  body: { state: SubscriptionState; dueOn?: string | null; graceDays?: number; reason?: string },
): Promise<SubscriptionView> =>
  call<SubscriptionView>(`/operator/clinics/${clinicId}/subscription`, { method: "PUT", body });

export const overdueClinics = (): Promise<OverdueClinic[]> =>
  call<OverdueClinic[]>("/operator/subscriptions/overdue");

export const billingSummary = (): Promise<BillingSummary> =>
  call<BillingSummary>("/operator/billing/summary");

/* ── payments and the operator's books (BP-D1 to BP-D3) ───────────────────── */

export interface ClinicPaymentView {
  amountCents: number;
  paidOn: string;
  method: string | null;
  note: string | null;
  recordedByName: string | null;
  recordedAt: string;
}

/** One month of a clinic's billing history. */
export interface ClinicMonth {
  /** `YYYY-MM`. */
  month: string;
  billCents: number | null;
  payment: ClinicPaymentView | null;
  overdue: boolean;
}

export interface ClinicPaymentHistory {
  currency: string;
  months: ClinicMonth[];
  paidCents: number;
  owedCents: number;
}

export interface OperatorBooks {
  currency: string;
  month: string;
  billedCents: number;
  collectedCents: number;
  outstandingCents: number;
  unpricedCount: number;
  rows: {
    clinicId: string;
    name: string;
    isDemo: boolean;
    billCents: number | null;
    payment: ClinicPaymentView | null;
  }[];
}

export const clinicPayments = (clinicId: string): Promise<ClinicPaymentHistory> =>
  call<ClinicPaymentHistory>(`/operator/clinics/${clinicId}/payments`);

/** Mark one month paid, or correct it. Records only: nothing is charged. */
export const markPaid = (
  clinicId: string,
  month: string,
  body: { amountCents: number; paidOn: string; method: string; note: string },
): Promise<ClinicPaymentHistory> =>
  call<ClinicPaymentHistory>(`/operator/clinics/${clinicId}/payments/${month}`, {
    method: "PUT",
    body,
  });

export const removePayment = (clinicId: string, month: string): Promise<ClinicPaymentHistory> =>
  call<ClinicPaymentHistory>(`/operator/clinics/${clinicId}/payments/${month}`, {
    method: "DELETE",
  });

export const operatorBooks = (month: string): Promise<OperatorBooks> =>
  call<OperatorBooks>(`/operator/accounting?month=${month}`);

/** What the help panel could not answer, grouped by wording (STORY-226). */
export const operatorHelpQuestions = (days: number): Promise<HelpQuestionsReport> =>
  call<HelpQuestionsReport>(`/operator/help-questions?days=${String(days)}`);
